import { LockClosedIcon } from '@heroicons/react/24/outline';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import cartUtils from '../../../../../utils/cart';
import formatUSD from '../../../../../utils/formatUSD';
import { isJpgImage } from '../../../../../utils/imageHelpers';
import { filterSelectedPosesForPicker } from '../../../../../utils/posePicker';
import PoseItem from '../../../shared/poseItem/PoseItem';
import { MagnifyingGlassPlusIcon } from '@heroicons/react/24/outline';
import { ZoomButtonWrapper } from '../../../productConfig/productConfigStyles';
import ZoomModal from './ZoomModal';

const PosesList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  justify-content: center;
  padding-left: 0;

  @media (max-width: 768px) {
    gap: 14px;
    justify-content: center;
  }
`;

const BackgroundOptionsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  justify-content: center;
  min-height: 150px;
`;

const EditingIndicator = styled.div`
  display: inline-flex;
  align-items: center;
  border: 1px solid #439be2;
  border-radius: 999px;
  color: #1769aa;
  font-weight: 700;
  margin: 0 auto 18px;
  padding: 8px 14px;
  width: fit-content;
`;

const LockContainer = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0 0 50px 50px;
  border-color: transparent transparent rgba(0, 0, 0, 0.5) transparent;
`;

const LockIcon = styled(LockClosedIcon)`
  position: absolute;
  bottom: -44px;
  right: 5px;
  height: auto;
  width: 20px;
  color: white;
`;

const AdjustedZoomButtonWrapper = styled(ZoomButtonWrapper)`
  &:hover {
    background-color: ##479de3;
  }
`;

const BackgroundsPosesSelector = ({
  addBackgroundSelection,
  addPoseSelection,
  allAvailablePosesForOrder,
  activeConfigSlotIndex,
  activeConfigStep,
  availablePoses,
  backgroundOptions,
  backgroundsPosesSelectorVisible,
  backgroundSelections,
  isPrepay,
  orderContainsSingleBackgroundUpgrade,
  posesCount,
  poseSelectionIds,
  poseSelections,
  product,
  productBackgroundOptionsCount,
  studentGallery,
  skipBackgroundSelection = false,
}) => {
  const { studentPreviewImageUrl } = useSelector(
    (state) => state.additionalData,
  );
  const [backgroundsCategoryIndex, setbackgroundsCategoryIndex] = useState(0);
  const { orders } = useSelector((state) => state.v3Order);
  const { studentId, hideSelectedPosesInOrderUi } = useSelector(
    (state) => state.additionalData,
  );
  const unlockedGroups = cartUtils.unlockedGroups(
    orders?.[studentId]?.cartItems || [],
  );
  const [isPoseRecentlySelected, setIsPoseRecentlySelected] = useState(false);
  const hasActiveConfigSlot =
    activeConfigSlotIndex !== null && activeConfigSlotIndex !== undefined;

  const [zoomedBackgroundId, setZoomedBackgroundId] = useState(null);

  useEffect(() => {
    if (activeConfigStep === 'background') {
      setbackgroundsCategoryIndex(0);
    }
  }, [activeConfigSlotIndex, activeConfigStep]);

  const handleZoomClick = (e, backgroundId) => {
    e.stopPropagation();
    setZoomedBackgroundId(
      zoomedBackgroundId === backgroundId ? null : backgroundId,
    );
  };

  if (!productBackgroundOptionsCount || productBackgroundOptionsCount === 0) {
    if (!skipBackgroundSelection || posesCount === 0) {
      return null;
    }
  }

  const BackgroundPrice = (background) => {
    if (product.triggers_free_backgrounds) {
      return true;
    }
    if (
      Number.isInteger(background?.price) &&
      background.price > 0 &&
      !orderContainsSingleBackgroundUpgrade // don't show price if the order already contains a single background upgrade
    ) {
      return (
        <>
          <br />
          <span className="bnl-bg-price">{formatUSD(background.price)}</span>
        </>
      );
    }

    return null;
  };

  const renderBackgroundCategoryTabs = () => {
    return backgroundOptions.map((backgroundCategory, index) => {
      let activeClass = '';

      if (index === backgroundsCategoryIndex) {
        activeClass = 'group-active';
      }

      return (
        <li
          key={backgroundCategory.id}
          className={`bnl-product-config__bg-selector__tab ${activeClass}`}
          onClick={() => setbackgroundsCategoryIndex(index)}
        >
          {backgroundCategory.name}
        </li>
      );
    });
  };

  const renderBackgroundOptions = (background) => {
    const isLocked =
      background.lock_type === 'unlockable' &&
      !unlockedGroups.some((set) => background.lock_group.includes(set)) &&
      !(
        product.lock_type === 'unlocker' &&
        product.lock_group.some((set) => background.lock_group.includes(set))
      );

    const lastPose =
      hasActiveConfigSlot && poseSelections[activeConfigSlotIndex]
        ? poseSelections[activeConfigSlotIndex]
        : poseSelections.length > 0
        ? poseSelections[poseSelections.length - 1]
        : null;

    const backgroundOptionStyle = {
      backgroundImage: `url(${background.image_thumbnail})`,
      backgroundPosition: 'center center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
      position: 'relative',
      cursor: isLocked ? 'default' : 'pointer',
      margin: '5px',
      minHeight: '150px',
      minWidth: '120px',
    };

    const imgStyle = {
      width: '100%', // Ensuring the overlay image covers the background
      height: '100%',
      objectFit: 'cover',
      position: 'absolute',
      top: 0,
      left: 0,
      userSelect: 'none',
      cursor: 'pointer',
    };

    const handleClick = (e) => {
      e.stopPropagation();
      if (isLocked) {
        return;
      }
      setIsPoseRecentlySelected(false);
      addBackgroundSelection(background);
      return false;
    };

    // Determine the pose image URL for layering check
    const poseImageUrl = lastPose
      ? lastPose.selected_crop
        ? lastPose.selected_crop.url
        : lastPose.url
      : studentPreviewImageUrl;

    // Don't layer JPG images over backgrounds - they don't have alpha transparency
    // JPGs will be converted to PNGs with transparency after order is placed
    const shouldShowPoseOverlay = poseImageUrl && !isJpgImage(poseImageUrl);

    return (
      <div key={background.id} style={{ minWidth: '120px' }}>
        <div
          className="bnl-product-config__bg-container col-lg-2"
          style={backgroundOptionStyle}
          onClick={handleClick}
        >
          {lastPose && shouldShowPoseOverlay && (
            <img
              src={
                lastPose.selected_crop
                  ? lastPose.selected_crop.url
                  : lastPose.url
              }
              alt="Overlay Pose"
              onContextMenu={(event) => event.preventDefault()}
              style={imgStyle}
              onClick={handleClick}
            />
          )}
          {!lastPose &&
            !isPrepay &&
            studentPreviewImageUrl &&
            shouldShowPoseOverlay && (
              <img
                src={studentPreviewImageUrl}
                alt="Overlay Pose"
                onContextMenu={(event) => event.preventDefault()}
                style={imgStyle}
                onClick={handleClick}
              />
            )}
          {isLocked && (
            <LockContainer>
              <LockIcon />
            </LockContainer>
          )}
          <AdjustedZoomButtonWrapper
            onClick={(e) => handleZoomClick(e, background.id)}
          >
            <MagnifyingGlassPlusIcon />
          </AdjustedZoomButtonWrapper>
        </div>
        <div className="bnl-bg-text text-center">
          {background.display_name}
          {BackgroundPrice(background)}
        </div>
      </div>
    );
  };

  const renderPoses = () => {
    // Prioritize availablePoses when posesCount > 1, otherwise prioritize non-empty studentGallery, fallback to availablePoses if studentGallery is empty or non-existent.
    let poseSource =
      posesCount > 1
        ? availablePoses
        : studentGallery && studentGallery?.length > 0
        ? studentGallery
        : availablePoses;

    if (allAvailablePosesForOrder?.length > 0) {
      poseSource = allAvailablePosesForOrder;
    }

    const selectedIdsForPicker = !hasActiveConfigSlot
      ? poseSelectionIds
      : poseSelectionIds.filter((_, index) => index !== activeConfigSlotIndex);

    poseSource = filterSelectedPosesForPicker(
      poseSource,
      selectedIdsForPicker,
      posesCount,
      hideSelectedPosesInOrderUi,
    );

    // Create a Set of selected crop IDs for efficient lookup
    const selectedCropIds = new Set(
      poseSelections
        .filter((p) => p.selected_crop?.id)
        .map((p) => p.selected_crop.id),
    );

    const poses = poseSource?.flatMap((pose) => {
      // TODO: handle group image type
      if (pose.cropped_images?.length > 0) {
        return pose.cropped_images.map((croppedImage, i) => {
          const isSelected =
            poseSelectionIds.includes(pose.id) &&
            selectedCropIds.has(croppedImage.id);
          return (
            <PoseItem
              key={`${pose.id}-${croppedImage.name}`}
              onClick={() => {
                setIsPoseRecentlySelected(true);
                addPoseSelection(pose.id, i);
              }}
              src={croppedImage.url}
              alt=""
              name={`${croppedImage.name}${isSelected ? ' ✓' : ''}`}
            />
          );
        });
      }
      return (
        <PoseItem
          key={pose.id}
          onClick={() => {
            setIsPoseRecentlySelected(true);
            addPoseSelection(pose.id);
          }}
          src={pose.url}
          onContextMenu={(event) => event.preventDefault()}
        />
      );
    });

    return <PosesList>{poses}</PosesList>;
  };

  const renderPosesConfig = () => {
    const hasPoseOptions =
      availablePoses?.length > 0 ||
      studentGallery?.length > 0 ||
      allAvailablePosesForOrder?.length > 0;

    if (activeConfigStep) {
      if (activeConfigStep !== 'pose' || !hasPoseOptions) {
        return null;
      }

      return <div className="bnl-product-config__poses">{renderPoses()}</div>;
    }

    // For digital downloads that skip background selection, show poses if required (but not in prepay)
    if (
      !isPrepay &&
      skipBackgroundSelection &&
      posesCount > 0 &&
      (availablePoses?.length > 0 ||
        studentGallery?.length > 0 ||
        allAvailablePosesForOrder?.length > 0)
    ) {
      return <div className="bnl-product-config__poses">{renderPoses()}</div>;
    }

    // Original logic for non-digital downloads
    if (
      !isPrepay &&
      posesCount > 0 &&
      (availablePoses?.length > 0 ||
        studentGallery?.length > 0 ||
        allAvailablePosesForOrder?.length > 0) &&
      (poseSelectionIds.length === backgroundSelections.length ||
        !backgroundOptions.length)
    ) {
      return <div className="bnl-product-config__poses">{renderPoses()}</div>;
    }
    return null;
  };

  const renderBackgroundsConfig = () => {
    // Skip background selection for digital downloads with all/no backgrounds
    if (skipBackgroundSelection) {
      return null;
    }

    if (activeConfigStep) {
      if (activeConfigStep !== 'background') {
        return null;
      }

      return (
        <div className="bnl-product-config">
          <ul className="bnl-product-config__bg-selector__tabs-container">
            {renderBackgroundCategoryTabs()}
          </ul>
          <BackgroundOptionsGrid className="equal-height">
            {backgroundOptions[
              backgroundsCategoryIndex
            ]?.background_images?.map((background) =>
              renderBackgroundOptions(background),
            )}
          </BackgroundOptionsGrid>
        </div>
      );
    }

    if (
      isPrepay ||
      (!isPrepay &&
        !availablePoses?.length &&
        !studentGallery?.length &&
        !allAvailablePosesForOrder?.length) ||
      (posesCount > 0 &&
        backgroundSelections.length < poseSelectionIds.length) ||
      isPoseRecentlySelected
    ) {
      return (
        <div className="bnl-product-config">
          <ul className="bnl-product-config__bg-selector__tabs-container">
            {renderBackgroundCategoryTabs()}
          </ul>
          <BackgroundOptionsGrid className="equal-height">
            {backgroundOptions[
              backgroundsCategoryIndex
            ]?.background_images?.map((background) =>
              renderBackgroundOptions(background),
            )}
          </BackgroundOptionsGrid>
        </div>
      );
    }

    return null;
  };

  const renderEditingIndicator = () => {
    if (!hasActiveConfigSlot || !activeConfigStep) {
      return null;
    }

    const totalSlots =
      activeConfigStep === 'background'
        ? productBackgroundOptionsCount
        : posesCount;
    const existingSelection =
      activeConfigStep === 'background'
        ? backgroundSelections[activeConfigSlotIndex]
        : poseSelections[activeConfigSlotIndex];
    const action = existingSelection ? 'Editing' : 'Selecting';
    const label = activeConfigStep === 'background' ? 'background' : 'pose';

    return (
      <EditingIndicator>
        {`${action} ${label} ${activeConfigSlotIndex + 1} of ${totalSlots}`}
      </EditingIndicator>
    );
  };

  const backgroundsConfig = renderBackgroundsConfig();
  const posesConfig = backgroundsConfig ? null : renderPosesConfig();

  return (
    <>
      <div
        className={`backgroundsPosesContainer ${
          backgroundsPosesSelectorVisible ? 'element-visible' : 'element-hidden'
        }`}
      >
        {renderEditingIndicator()}
        {posesConfig}
        {backgroundsConfig}
      </div>
      <ZoomModal
        open={!!zoomedBackgroundId}
        imageUrl={
          backgroundOptions
            .flatMap((cat) => cat.background_images || [])
            .find((bg) => bg.id === zoomedBackgroundId)?.image_thumbnail
        }
        onClose={() => setZoomedBackgroundId(null)}
      />
    </>
  );
};

BackgroundsPosesSelector.propTypes = {
  addBackgroundSelection: PropTypes.func.isRequired,
  addPoseSelection: PropTypes.func.isRequired,
  allAvailablePosesForOrder: PropTypes.array,
  activeConfigSlotIndex: PropTypes.number,
  activeConfigStep: PropTypes.string,
  availablePoses: PropTypes.array,
  backgroundOptions: PropTypes.array.isRequired,
  backgroundsPosesSelectorVisible: PropTypes.bool.isRequired,
  backgroundSelections: PropTypes.array.isRequired,
  isPrepay: PropTypes.bool.isRequired,
  posesCount: PropTypes.number.isRequired,
  poseSelectionIds: PropTypes.array.isRequired,
  poseSelections: PropTypes.array.isRequired,
  productBackgroundOptionsCount: PropTypes.number.isRequired,
  studentGallery: PropTypes.array,
  orderContainsSingleBackgroundUpgrade: PropTypes.bool.isRequired,
  skipBackgroundSelection: PropTypes.bool,
};

export default BackgroundsPosesSelector;
